import { Fragment, useRef, useState } from 'react';
import type { IUserSettingsSubmittalFormatRow } from 'app/services/userSettingsApi';
import { useRailSortable } from '../../useRailSortable';
import { deleteButtonWantsTop } from './submittalModel';

/**
 * Column 1 of the Submittal tab — `submittal.tmpl:5-35`. The ordered table of
 * contents of the submittal package: what actually gets rendered, in order.
 *
 * ★ THIS COLUMN HAS TWO TEMPLATES, AND THEY ARE NOT THE SAME.
 *  · the full render (`submittal.tmpl:19-33`) emits
 *    `<div class="row ui-sortable" id="submittal-format-list-wrapper-row">`,
 *    the N rows, and a trailing `<div class="select-row empty-row"></div>`
 *    sentinel — the "append to the end" drop target;
 *  · `render_selects()` (`submittal.js:196-219`) replaces the wrapper's whole
 *    innerHTML with `submittal.format.list.tmpl`, which has **no `ui-sortable`
 *    class and NO `.empty-row`**, and lists the row classes in a different
 *    order (`select-row select-row-click submittal-item-for-drag` instead of
 *    `select-row submittal-item-for-drag select-row-click`).
 * `render_selects` is reached from exactly one place in the submittal view —
 * deleting a User Defined Field (`:515`) — so after that one action the rail
 * legitimately loses its empty-row sentinel until the next full render. Both
 * shapes are reproduced; `variant` selects between them.
 *
 * ★ …BUT `ui-sortable` IS IN THE LIVE DOM FOR BOTH, because both renders end in
 * `makeSortable()` (`:190` and `:210`) and jQuery-UI's `.sortable()` stamps
 * `ui-sortable` on its container and `ui-sortable-handle` on every item
 * (appended to the END of the class list, which is where they are emitted
 * below). Neither class has a single CSS reference in the 69 KB
 * `user_settings/styles.css` — nothing visual depends on them — but they are on
 * every rail row on the real page, so they are rendered for DOM parity. The one
 * row that legitimately LACKS `ui-sortable-handle` is `createNewField`'s
 * hand-injected one: it is written into the DOM from `templateSelectItem` with
 * no `makeSortable()` call after it, so it stays unstamped until the full
 * re-render replaces it. `domIdOverrides` identifies exactly that row.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ U-d — HOW THE DRAG WORKS HERE, AND WHY IT IS NOT jQuery-UI ★★
 *
 * Legacy runs `makeSortable()` (`submittal.js:832-881`): a jQuery-UI
 * `sortable({items:'.submittal-item-for-drag', axis:'y'})` over this container,
 * plus `draggable({connectToSortable})` on every library anchor — which carries
 * the SAME `.submittal-item-for-drag` class, which is the entire trick that lets
 * a library item land in the sortable and be recognised as one of its items.
 * One `stop` callback then branches on `self.dropping` (set by the draggable's
 * `start`): true → drop-to-create, false → reorder.
 *
 * BOTH HALVES ARE POINTER DRAGS, because both jQuery-UI widgets are.
 *
 *  · the INTERNAL reorder is `useRailSortable` — `axis: "y"`, the helper's
 *    containment, the `ui-sortable-placeholder` hole and a list whose geometry
 *    does not move mid-drag. The first port did it with native HTML5 DnD and
 *    spliced the real row into the target slot on every `dragover`, which is
 *    what made the rail shuffle and overlap under the cursor.
 *  · the LIBRARY drag is `useLibraryDrag` (2026-09-09), which owns the clone
 *    helper AND `libraryPlaceholderIndex` — the gap this rail opens for it. It
 *    too started as native DnD; that gave the dragged row no visible helper,
 *    let the browser accept the drop into any text input on the page, and made
 *    the gap blink shut every time the pointer crossed a row boundary. See that
 *    hook's banner.
 *
 * So NOTHING on this page binds a `drag*` handler any more. If you are looking
 * for where a drop is accepted, it is a `mouseup` in one of those two hooks.
 *
 * jQuery-UI's `cancel: "input,textarea,button,select,option"` is reproduced
 * literally in both halves: a press on the trash button never starts a sort,
 * which is why clicking the trash emits no drag PATCH (see Q22 below).
 *
 * ★ Q22 — A PLAIN CLICK ON A ROW EMITS A DRAG PATCH.
 * jQuery-UI fires `stop` for a zero-distance drag, so a click on a rail row puts
 * a full (no-op) `/drag/` PATCH on the wire carrying the identical ordering.
 * Native DnD has no such event, so it is reproduced deliberately: the row's
 * `onClick` calls `onRowPlainClick()` — suppressed only where jQuery-UI's own
 * `cancel` option would have suppressed it (a press on the trash `<button>`),
 * and after a real drag, where jQuery-UI sets `preventClickEvent`.
 * ═════════════════════════════════════════════════════════════════════════════
 */

export type FormatRailVariant = 'render' | 'renderSelects';

interface FormatRailProps {
  rows: IUserSettingsSubmittalFormatRow[];
  /** `render_selects`' template drops the sentinel and the `ui-sortable` class. */
  variant: FormatRailVariant;
  /** `self.active_row_id` — the `row-<id>` of the `.active` row, or `null`. */
  activeRowId: string | null;
  /** Rename overlay: `saveUserDefinedFieldData` (`:459-465`) rewrites the label
   * of every row whose `data-target` matches the renamed UDF, WITHOUT a
   * re-render. Keyed by the row's `data-target` (`object_id`, as a string). */
  labelOverrides: Record<string, string>;
  /**
   * ★ U-d — rows whose DOM id and trash `data-id` must be rendered from
   * something OTHER than `row.id`. Exactly one row ever qualifies: the one
   * `createNewField` injects by hand between the drag PATCH and the full
   * re-render, because `templateSelectItem` is handed `{id: target_id, …}`
   * (`submittal.js:808-815`) — the **library item's** id, not the format row's.
   * So the injected row is `id="row-13"` for a user drop and, since CMS anchors
   * have no `data-id` at all, the bare `id="row-"` for a CMS drop. Both are
   * reproduced; the row lives for one round-trip and is then replaced.
   * Keyed by the synthetic `row.id` `SubmittalTab` gives the injected row.
   */
  domIdOverrides: Record<number, string>;
  /** Row ids whose label overflows — `showTooltip` (`:887-889`) puts
   * `.with_tooltip_element` on the `.select-row` itself (the span's parent). */
  tooltipRowIds: ReadonlySet<number>;
  /** `rowClick` (`:895-915`). `domId` is what legacy reads back out of the DOM
   * as `self.active_row_id` (`:899`), which for the injected row is not
   * `row-<row.id>` — see `domIdOverrides`. */
  onRowClick: (row: IUserSettingsSubmittalFormatRow, domId: string) => void;
  /** `mouseover .select-row-click[data-model="submittaluserdefinedfield"]` →
   * `startLoadPdf` (`:891-894`). Bound on every row here and filtered by model
   * inside `SubmittalTab`, which is the same net effect as the attribute
   * selector jQuery delegates on. */
  onRowMouseOver: (row: IUserSettingsSubmittalFormatRow) => void;
  /** `#submittal-format-list-wrapper`'s `onscroll` → `check_scroll` (`:781-785`). */
  onWrapperScroll: (wrapper: HTMLDivElement) => void;
  /** Handed up so `addUserDefinedField` can capture/restore `scrollTop`
   * (`submittal.js:264,268`) and `showTooltip` can measure the labels. */
  wrapperRef: React.MutableRefObject<HTMLDivElement | null>;
  labelRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;

  // ── U-d ────────────────────────────────────────────────────────────────
  /** The sortable container itself — `#submittal-format-list-wrapper-row`, the
   * element `connectToSortable` names. Handed up because the library drag
   * (`useLibraryDrag`) has to measure it: it is the one region a dragged
   * library anchor may be released over. */
  railRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Where the LIBRARY drag would insert, or `null` when its helper is not over
   * the rail — jQuery-UI's `ui-sortable-placeholder`, owned by
   * `useLibraryDrag` because that widget is the one that moves it. An internal
   * drag does not use this: the row itself moves, opening the same gap. */
  libraryPlaceholderIndex: number | null;
  /** Live move of the PLACEHOLDER during an internal drag — the sortable
   * shuffling its items around the hole. The rail's rendered order IS the
   * payload builder's input, exactly as legacy reads the final DOM in `stop`. */
  onRowsMove: (from: number, to: number) => void;
  /** The internal-drag half of `stop` (`:851-861`) → §6 W7. */
  onReorderCommit: () => void;
  /** ★ Q22 — the zero-distance `stop`. Same payload as `onReorderCommit`. */
  onRowPlainClick: () => void;
  /** `click #submittal .delete` → `render_delete_popup_submittal` (`:638-646`).
   * ★ Q4 — called TWICE per click: once for the `<i class="icon-trash delete">`
   * and once for the `<button class="… delete">` it bubbles into. Legacy gets
   * the double because jQuery delegation fires a delegated handler once per
   * matching ancestor and `render_delete_popup_submittal` never calls
   * `stopPropagation` (unlike `render_popup`); React gets it because both
   * elements carry the handler and neither stops the bubble. Two identical
   * popups, one DELETE. */
  onDeleteClick: (domId: string) => void;
}

/** jQuery-UI sortable's default `cancel` option — a press on any of these never
 * starts a sort. Reproduced verbatim so the trash button stays a button. */
const SORTABLE_CANCEL = 'input,textarea,button,select,option';

const FormatRail = ({
  rows,
  variant,
  activeRowId,
  labelOverrides,
  domIdOverrides,
  tooltipRowIds,
  onRowClick,
  onRowMouseOver,
  onWrapperScroll,
  wrapperRef,
  labelRefs,
  railRef,
  libraryPlaceholderIndex,
  onRowsMove,
  onReorderCommit,
  onRowPlainClick,
  onDeleteClick,
}: FormatRailProps) => {
  /**
   * ★ Q20's `.top` flip. Legacy toggles the class with `classList` on a node it
   * also re-renders from a template; here it is state so React's declarative
   * `className` cannot silently wipe it on the next render.
   */
  const [topRows, setTopRows] = useState<ReadonlySet<number>>(() => new Set());

  /**
   * The gap a LIBRARY drag opens, i.e. where the item would land — jQuery-UI's
   * `ui-sortable-placeholder` (an invisible clone of the dragged item that the
   * sortable keeps in the list so the rows part around it). `null` when no
   * library drag is over the rail. An internal drag does not use this: the row
   * itself moves, which opens the same gap in the same place.
   *
   * ★ It arrives as a PROP now. The library drag is a pointer drag
   * (`useLibraryDrag`, 2026-09-09) driven from the tab, so the widget that owns
   * the helper also owns the hole it opens — as jQuery-UI does, where one
   * `mousemove` moves both. The rail used to derive it from `dragover`, which
   * is why the gap blinked shut whenever the pointer crossed a row boundary.
   */
  const placeholderIndex = libraryPlaceholderIndex;
  /**
   * ★ U-d (2026-09-09) — the INTERNAL reorder: the lifted row, its Y-only
   * travel, its containment and the hole it leaves behind. Legacy's
   * `sortable({axis: 'y'})`.
   */
  const sortable = useRailSortable({
    rowCount: rows.length,
    scrollerRef: wrapperRef,
    cancelSelector: SORTABLE_CANCEL,
    onRowsMove,
    onCommit: onReorderCommit,
  });
  /**
   * ★ Q22's OTHER half — jQuery-UI's `distance: 1`.
   *
   * `$.ui.mouse._mouseMove` only starts (and therefore only ever stops) a sort
   * once `Math.max(|dx|, |dy|) >= options.distance`, which defaults to 1. So a
   * press-and-release that never moves the pointer a single pixel fires NO
   * `stop` and puts NO `/drag/` PATCH on the wire — verified live on the legacy
   * page: 0 px → no request, 1 px → the full no-op reorder PATCH.
   *
   * Native DnD has no such threshold, so the press point is recorded here and
   * the distance is re-tested at click time. Without it React emits one extra
   * PATCH per perfectly-still click, which a request-counting verifier sees.
   */
  const pressPointRef = useRef<{ x: number; y: number } | null>(null);

  const handleDeleteMouseOver = (rowId: number, target: HTMLElement) => {
    const wants = deleteButtonWantsTop(target);
    setTopRows((prev) => {
      if (wants === prev.has(rowId)) return prev;
      const next = new Set(prev);
      if (wants) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  /**
   * ★ Where the dragged row's own slot is reproduced.
   *
   * jQuery-UI lifts the item (`position: absolute`, `axis: 'y'`) and leaves an
   * invisible `ui-sortable-placeholder` of the SAME height in the list. So the
   * list keeps exactly n slots of 40px for the whole drag — the rows part
   * around the hole, nothing re-flows, and `useRailSortable`'s cached geometry
   * stays true from `mousedown` to `mouseup`.
   *
   * ★ `stop` FIRES ON EVERY MOUSEUP that followed a started sort, so legacy
   * PATCHes even when the row landed back where it started. `onCommit` is wired
   * straight to `onReorderCommit` with deliberately NO "skip if the order did
   * not change" guard — `useRowDragReorder` has one, this must not.
   */
  const internalDragIndex = sortable.drag?.index ?? null;

  return (
    <div className="col-xs-3 full-height left-column submittal-content-submittal-format">
      {/* `submittal.tmpl:6-16`. Permanently invisible: the whole header row is
          `hidden`, and Q18 — `#add_field_to_structure` is additionally
          hard-coded `hide` AND the submittal view binds no handler to it at all
          (only proposal binds `addStructureField`). Rendered anyway because it
          is in the markup. */}
      <div className="row block-header proposal-format-header hidden">
        <div className="col-xs-12 col-md-8 col-lg-8">
          <i className="icon-baseline-description-24px" />
          <span className="block-title">Submittal Format</span>
        </div>
        <div className="col-xs-12 col-md-4 col-lg-4 block-header-button-add">
          <button id="add_field_to_structure" className="btn-add btn add hide">
            <i style={{ fontSize: '17px', paddingRight: '6px' }}>+</i> ADD
          </button>
        </div>
      </div>
      <div className="drag-note">Drag and drop to add or move a field.</div>
      <div
        className="left-column-user-settings-scrollable"
        id="submittal-format-list-wrapper"
        ref={wrapperRef}
        onScroll={(e) => onWrapperScroll(e.currentTarget)}
      >
        {/*
          ★★ THE DROP ZONE IS THIS CONTAINER, NOT THE SCROLL WRAPPER ABOVE IT —
          and that is a deliberate parity decision, not an oversight.

          `#submittal-format-list-wrapper-row` is the element jQuery-UI's
          sortable is bound to, so its BOX is exactly the area a drop can land
          in. With the `.empty-row` sentinel present that box extends one row
          below the last real row, which is what makes "append to the end"
          reachable. After a UDF delete, `render_selects` re-renders this rail
          from a template that has NO sentinel (see the banner), the container
          shrinks to the rows, and the empty space below them stops being a drop
          target until the next full render. Legacy has exactly that gap.
          Measuring the scroll wrapper instead would quietly invent an
          append-to-end path legacy does not have in that state, so `railRef` is
          bound HERE — `useLibraryDrag` hit-tests exactly this box. Dropping on
          the BOTTOM HALF OF THE LAST ROW still appends in both stacks, which is
          why the gap is narrow enough to be invisible in normal use.
        */}
        <div
          /* `submittal.tmpl:19` hard-codes `ui-sortable`;
             `submittal.format.list.tmpl:1` does not, and `makeSortable()` puts
             it back one line later — so it is present either way. */
          className="row ui-sortable"
          id="submittal-format-list-wrapper-row"
          ref={(el) => {
            sortable.containerRef.current = el;
            railRef.current = el;
          }}
          /* The helper is absolutely positioned, so the sortable container has
             to be its offset parent — which is also what keeps it inside the
             list. Only while a drag is in flight, so no stacking context is
             created at rest. */
          style={sortable.drag ? { position: 'relative' } : undefined}
        >
          {rows.map((row, index) => {
            const target = row.object_id == null ? '' : String(row.object_id);
            const label = labelOverrides[target] ?? row.name ?? '';
            /** Handlebars' `{{id}}` — normally the format row's id, but the
             * library item's for the hand-injected row (see `domIdOverrides`). */
            const domId = domIdOverrides[row.id] ?? String(row.id);
            /** A row carrying a DOM-id override is the hand-injected one, and
             * `createNewField` never re-runs `makeSortable()` — so it is the one
             * rail row without jQuery-UI's item class. */
            const injected = domIdOverrides[row.id] !== undefined;
            return (
              /* A keyed Fragment, NOT a wrapper element: `styles.css` selects
                 `#submittal-format-list-wrapper-row > div.submittal-item-for-drag`
                 (and legacy's own renumbering loop uses that exact child
                 selector), so the rows must stay direct children. */
              <Fragment key={row.id}>
                {placeholderIndex === index && <FormatRailPlaceholder />}
                {/* The lifted row's own hole — see `internalDragIndex`. */}
                {internalDragIndex === index && sortable.drag && (
                  <FormatRailPlaceholder height={sortable.drag.height} />
                )}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/mouse-events-have-key-events */}
                <div
                  className={[
                    'col-xs-12',
                    'select-row',
                    ...(variant === 'render'
                      ? ['submittal-item-for-drag', 'select-row-click']
                      : ['select-row-click', 'submittal-item-for-drag']),
                    activeRowId === `row-${domId}` ? 'active' : '',
                    tooltipRowIds.has(row.id) ? 'with_tooltip_element' : '',
                    // jQuery-UI's `_setHandleClassName`, appended last.
                    injected ? '' : 'ui-sortable-handle',
                    // jQuery-UI stamps this on the item it lifts;
                    // `styles.css:2134` tints it `#f2efed` while it travels.
                    internalDragIndex === index ? 'ui-sortable-helper' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={sortable.helperStyle(index)}
                  id={`row-${domId}`}
                  data-index={index}
                  data-name={row.name ?? ''}
                  data-target={target}
                  data-model={row.model ?? ''}
                  data-cms={row.cms_field_name ?? ''}
                  /* ★ NO `draggable` attribute — legacy's rows have none
                     either, because jQuery-UI drives the sort off raw mouse
                     events, and so do BOTH pointer drags on this page:
                     `useRailSortable` here and `useLibraryDrag` in the middle
                     column. No `drag*` handler is bound anywhere on the rail
                     for the same reason. */
                  onMouseDown={(e) => {
                    pressPointRef.current = { x: e.clientX, y: e.clientY };
                    sortable.onRowMouseDown(e, index);
                  }}
                  onClick={(e) => {
                    // ★ Q22 — see the banner. Suppressed exactly where
                    // jQuery-UI would never have started (and therefore never
                    // stopped) a sort: a press on a `cancel`ed control, a press
                    // that never met the `distance: 1` threshold, the click
                    // trailing a real drag (`preventClickEvent`), and — U-f — a
                    // PROGRAMMATIC click, which has no pointer interaction
                    // behind it at all.
                    //
                    // ★ U-f: `detail === 0` identifies `HTMLElement.click()`,
                    // i.e. the click the ↑/↓ walk synthesises 300 ms after each
                    // keypress (`useFilterNavigation.ts`). jQuery-UI's sortable
                    // runs off `mousedown`/`mouseup` and never sees such a click,
                    // so legacy emits NO `/drag/` PATCH while arrowing —
                    // measured: a 25-press walk from the top of the rail to the
                    // bottom put ZERO non-GET requests on the wire. Without this
                    // test the press point is `null` (no `mousedown` ever
                    // happened), `moved` reads `true`, and EVERY arrow keypress
                    // would emit a full reorder. Real clicks report `detail >= 1`.
                    const press = pressPointRef.current;
                    const moved =
                      press === null ||
                      Math.max(Math.abs(e.clientX - press.x), Math.abs(e.clientY - press.y)) >= 1;
                    pressPointRef.current = null;
                    // `preventClickEvent` — jQuery-UI swallows the click that
                    // trails a real drag OUTRIGHT, so a reorder neither emits
                    // Q22's PATCH nor moves the `.active` row.
                    if (sortable.justDraggedRef.current) return;
                    if (
                      e.detail !== 0 &&
                      moved &&
                      !(e.target as HTMLElement).closest(SORTABLE_CANCEL)
                    ) {
                      onRowPlainClick();
                    }
                    onRowClick(row, domId);
                  }}
                  onMouseOver={() => onRowMouseOver(row)}
                >
                  <span
                    className="submittal_format_field"
                    ref={(el) => {
                      if (el) labelRefs.current.set(row.id, el);
                      else labelRefs.current.delete(row.id);
                    }}
                  >
                    {label}
                  </span>
                  <div className="dropdown dropdownprop-format">
                    <div className="">
                      {/* ── §6 W9 delete + ★ Q4's double popup ─────────────
                          BOTH the button and the nested icon call
                          `onDeleteClick`, and neither stops the bubble, so one
                          click opens two identical popups — and then bubbles on
                          to the row's `onClick` for `rowClick`, exactly as the
                          three delegated legacy handlers do. */}
                      {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                      <button
                        data-parent=".left-column-user-settings-scrollable"
                        data-id={domId}
                        className={`ka-has-tooltip btn  dropdown-toggle icon-more-actions btn-more delete delete-btn-content${
                          topRows.has(row.id) ? ' top' : ''
                        }`}
                        type="button"
                        data-toggle="dropdown"
                        tooltip="Delete"
                        onMouseOver={(e) => handleDeleteMouseOver(row.id, e.target as HTMLElement)}
                        onClick={() => onDeleteClick(domId)}
                      >
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
                        <i data-id={domId} className="icon-trash delete" onClick={() => onDeleteClick(domId)} />
                      </button>
                    </div>
                  </div>
                </div>
              </Fragment>
            );
          })}
          {placeholderIndex !== null && placeholderIndex >= rows.length && <FormatRailPlaceholder />}
          {/* The "append to the end" drop target. Present in `submittal.tmpl:32`
              and ABSENT from `submittal.format.list.tmpl` — see the banner. */}
          {variant === 'render' && <div className="select-row empty-row" />}
        </div>
      </div>
    </div>
  );
};

/**
 * jQuery-UI's `ui-sortable-placeholder`: the invisible stand-in the sortable
 * leaves in the list so the rows part around the incoming item.
 *
 * ⚠ Two deliberate simplifications. jQuery-UI's `_createPlaceholder` clones the
 * dragged element's TAG NAME and CLASS LIST, so a library drop really opens an
 * `<a>`-shaped gap the height of the library anchor, not of a rail row; and it
 * has no `data-index`, so it never disturbs the renumbering. The second half is
 * preserved (no `data-index` here either, and `data-index` is the map position
 * so the placeholder cannot shift it); the first is not — a row-shaped gap is
 * what the user is actually aiming at, and the element is invisible either way.
 */
const FormatRailPlaceholder = ({ height }: { height?: number }) => (
  <div
    className="col-xs-12 select-row submittal-item-for-drag ui-sortable-placeholder"
    /* An internal drag pins the hole to the lifted row's own measured height,
       so the list's total height cannot change while it travels. A library
       drag leaves it to `.select-row`'s 40px, as before. */
    style={
      height === undefined
        ? { visibility: 'hidden' }
        : { visibility: 'hidden', height, minHeight: height, maxHeight: height }
    }
  />
);

export default FormatRail;
